import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { Tooltip } from '@mui/material';
import {
    StyledTextField,
    StyledTextfieldLabel
} from '../../../components/muiOverwriteComponents/MuiTextfield';
import { StyledSelect } from '../../../components/muiOverwriteComponents/MuiSelect';
import { IImageEditorFirm } from '../Entities';

export type ImageEditorFirmsModalLabels = {
    textfieldLegend: string;
    dialogTitle: string;
    selectLegend: string;
    cancelButton: string;
    saveButton: string;
    defaultCheckboxLabel: string;
    expressCheckboxLabel: string;
    deselectionIsNotAllowed: string;
};

type ImageEditorFirmsModalProps = {
    editorFirm?: IImageEditorFirm;
    showModal: boolean;
    onClose: () => void;
    labels: ImageEditorFirmsModalLabels;
    onSave: (imageEditingFirm?: Partial<IImageEditorFirm>) => void;
};

export const ImageEditorFirmsModal = ({
    editorFirm,
    showModal,
    onClose,
    labels,
    onSave
}: ImageEditorFirmsModalProps) => {
    const checkboxLabel = { inputProps: { 'aria-label': 'mui checkbox' } };
    const [firmName, setFirmName] = React.useState<string>(editorFirm ? editorFirm.name : '');
    const [selectedOption, setSelectedOption] = React.useState<string>(
        editorFirm && editorFirm.downloadStorageKey !== null ? editorFirm.downloadStorageKey : ''
    );
    const [isDefault, setIsDefault] = React.useState<boolean>(
        editorFirm ? Boolean(editorFirm.brokerFirmDefault) : false
    );
    const [isExpressDefault, setIsExpressDefault] = React.useState<boolean>(
        editorFirm ? Boolean(editorFirm.brokerFirmDefaultExpress) : false
    );

    useEffect(() => {
        if (editorFirm) {
            setFirmName(editorFirm.name);

            if (editorFirm.downloadStorageKey != null) {
                setSelectedOption(editorFirm.downloadStorageKey);
            }

            setIsDefault(editorFirm.brokerFirmDefault);
            setIsExpressDefault(editorFirm.brokerFirmDefaultExpress);
        }
    }, [editorFirm]);

    const handleEditFirmName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirmName(event.target.value);
    };

    const handleSelect = (event) => {
        if (event.target.value === true) {
            return setSelectedOption(event.target.value);
        }

        setSelectedOption(event.target.value);
    };

    const handleSave = () => {
        onSave({
            id: editorFirm ? editorFirm.id : undefined,
            name: firmName,
            downloadStorageKey: selectedOption,
            isDiscontinued: editorFirm ? editorFirm.isDiscontinued : undefined,
            brokerFirmDefault: isDefault,
            brokerFirmDefaultExpress: isExpressDefault
        });
    };

    const handleDefaultCheckbox = (event) => {
        setIsDefault(event.target.checked);
    };

    const handleDefaultExpressCheckbox = (event) => {
        setIsExpressDefault(event.target.checked);
    };

    const CloseDialogButton = () => {
        return (
            <IconButton
                aria-label="close"
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500]
                }}
                onClick={onClose}
            >
                <CloseIcon />
            </IconButton>
        );
    };

    const DefaultCheckboxes = () => {
        return (
            <Box display={'flex'} flexDirection={'column'} sx={{ marginTop: '12px' }}>
                {editorFirm && editorFirm.brokerFirmDefault === true ? (
                    <Tooltip
                        componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}
                        title={labels.deselectionIsNotAllowed}
                    >
                        <FormControlLabel
                            label={
                                <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>
                                    {labels.defaultCheckboxLabel}
                                </Typography>
                            }
                            sx={{
                                margin: 0,
                                '& .MuiSvgIcon-root': {
                                    fontSize: '20px'
                                }
                            }}
                            control={
                                <Checkbox
                                    {...checkboxLabel}
                                    disabled={editorFirm && editorFirm.brokerFirmDefault}
                                    defaultChecked={isDefault}
                                    onChange={handleDefaultCheckbox}
                                />
                            }
                        />
                    </Tooltip>
                ) : (
                    <FormControlLabel
                        label={
                            <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>
                                {labels.defaultCheckboxLabel}
                            </Typography>
                        }
                        sx={{
                            margin: 0,
                            '& .MuiSvgIcon-root': {
                                fontSize: '20px'
                            }
                        }}
                        control={
                            <Checkbox
                                {...checkboxLabel}
                                disabled={editorFirm && editorFirm.brokerFirmDefault}
                                defaultChecked={isDefault}
                                onChange={handleDefaultCheckbox}
                            />
                        }
                    />
                )}
                <FormControlLabel
                    label={
                        <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>
                            {labels.expressCheckboxLabel}
                        </Typography>
                    }
                    sx={{
                        margin: 0,
                        '& .MuiSvgIcon-root': {
                            fontSize: '20px'
                        }
                    }}
                    control={
                        <Checkbox
                            {...checkboxLabel}
                            defaultChecked={isExpressDefault}
                            onChange={handleDefaultExpressCheckbox}
                        />
                    }
                />
            </Box>
        );
    };

    const SubmitButtons = () => {
        return (
            <Box display={'flex'} justifyContent={'flex-end'} sx={{ marginTop: '20px' }}>
                <Button
                    variant={'outlined'}
                    sx={{ margin: '6px', width: '92px', height: '42px', fontSize: '15px' }}
                    onClick={onClose}
                >
                    {labels.cancelButton}
                </Button>
                <Button
                    variant={'contained'}
                    color={'primary'}
                    sx={{ margin: '6px', width: '92px', height: '42px', fontSize: '15px' }}
                    onClick={handleSave}
                >
                    {labels.saveButton}
                </Button>
            </Box>
        );
    };

    const SelectDownloadStorageOverride = () => {
        return (
            <Select
                id={'select-label'}
                value={selectedOption}
                input={<StyledSelect />}
                displayEmpty={true}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            '&& .MuiMenuItem-root': {
                                padding: 2,
                                fontSize: '16px',
                                alignItems: 'center'
                            },
                            '&& .MuiInputLabel-root': {
                                display: 'flex',
                                justifyContent: 'center'
                            }
                        }
                    }
                }}
                onChange={handleSelect}
            >
                <MenuItem key={'defaultOption'} value={''}></MenuItem>
                {editorFirm
                    ? editorFirm.storageAccounts.map((account: any) => {
                          return (
                              <MenuItem key={account.key} value={account.key}>
                                  {account.name}
                              </MenuItem>
                          );
                      })
                    : null}
            </Select>
        );
    };

    return (
        <Dialog open={showModal} margin={0} maxWidth={'412px'} maxheight={'421px'}>
            <DialogTitle sx={{ fontSize: '20px', fontWeight: '500', lineHeight: '32px' }}>
                {labels.dialogTitle}
            </DialogTitle>
            <CloseDialogButton />
            <DialogContent sx={{ maxWidth: '390px' }}>
                <Box display={'flex'} flexDirection={'column'}>
                    <FormControl sx={{ paddingBottom: '20px' }}>
                        <InputLabel shrink sx={{ zIndex: 100, backgroundColor: 'white' }}>
                            <StyledTextfieldLabel>{labels.textfieldLegend}</StyledTextfieldLabel>
                        </InputLabel>
                        <StyledTextField
                            variant="outlined"
                            value={firmName}
                            onChange={handleEditFirmName}
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel
                            shrink
                            sx={{ zIndex: 100, backgroundColor: 'white' }}
                            id="select-label"
                        >
                            <StyledTextfieldLabel>{labels.selectLegend}</StyledTextfieldLabel>
                        </InputLabel>
                        <SelectDownloadStorageOverride />
                    </FormControl>
                </Box>
                <DefaultCheckboxes />
                <SubmitButtons />
            </DialogContent>
        </Dialog>
    );
};
